/**
 * Rhythmz — Agent Panel v2
 * ────────────────────────
 * Renders the 3 AI agents (Music, Visual DJ, Research) in the left dock
 * with live status, thought stream, and command buttons.
 */

import type { AgentState } from './AIAgentCoordinator';

const AGENT_CONFIG = [
  { key: 'Music AI', icon: '🎵', color: '#f84', commands: [
    { label: 'Compose', cmd: 'compose', arg: 'energetic techno' },
    { label: 'Drums', cmd: 'drums', arg: 'house' },
    { label: 'Ambient', cmd: 'compose', arg: 'calm ambient pad' },
  ]},
  { key: 'Visual DJ AI', icon: '🎨', color: '#4af', commands: [
    { label: 'Neon', cmd: 'preset', arg: 'neon storm' },
    { label: 'Galaxy', cmd: 'preset', arg: 'cosmic galaxy' },
    { label: 'Laser', cmd: 'effect', arg: 'laserGrid' },
  ]},
  { key: 'Research AI', icon: '📚', color: '#af8', commands: [
    { label: 'WebGPU', cmd: 'research', arg: 'WebGPU visual effects 2026' },
    { label: 'Discover', cmd: 'discover', arg: '' },
    { label: 'Music', cmd: 'research', arg: 'music production techniques 2026' },
  ]},
];

export class AgentPanel {
  private container: HTMLElement;
  private agentEls: Record<string, { status: HTMLElement; action: HTMLElement; log: HTMLElement }> = {};
  private thoughtLog: HTMLElement;
  private onCommand: ((cmd: string, arg: string) => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.container.innerHTML = this.buildHTML();

    for (const cfg of AGENT_CONFIG) {
      const cls = cfg.key.replace(/\s+/g, '').toLowerCase();
      this.agentEls[cfg.key] = {
        status: this.container.querySelector(`.ap-${cls} .ap-status`)!,
        action: this.container.querySelector(`.ap-${cls} .ap-action`)!,
        log: this.container.querySelector(`.ap-${cls} .ap-log`)!,
      };
    }

    this.thoughtLog = this.container.querySelector('.ap-thought-log')!;

    // Wire command buttons with loading state
    this.container.querySelectorAll<HTMLButtonElement>('.ap-cmd-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const cmd = btn.dataset.cmd ?? '';
        const arg = btn.dataset.arg ?? '';
        const origText = btn.textContent;
        // Show loading state
        btn.disabled = true;
        btn.style.opacity = '0.6';
        btn.textContent = '⏳ ' + (origText ?? '');
        this.onCommand?.(cmd, arg);
        // Reset after 8s (commands are async but we don't track completion here)
        setTimeout(() => {
          btn.disabled = false;
          btn.style.opacity = '1';
          btn.textContent = origText;
        }, 8000);
      });
    });
  }

  setCommandHandler(fn: (cmd: string, arg: string) => void): void {
    this.onCommand = fn;
  }

  private buildHTML(): string {
    return `
      <div class="agent-panel" style="font-family:var(--font);font-size:12px;color:#ddd;padding:10px 12px;">
        <div style="font-weight:700;margin-bottom:10px;color:#8cf;font-size:14px;">🤖 AI Agents</div>
        <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:10px;">
          ${AGENT_CONFIG.map((a) => this.renderAgent(a)).join('')}
        </div>
        <div style="opacity:0.6;font-size:10px;margin:6px 0 3px;text-transform:uppercase;letter-spacing:0.05em;">Thought Stream</div>
        <div class="ap-thought-log" style="max-height:140px;overflow-y:auto;background:rgba(0,0,0,0.4);border-radius:6px;padding:6px;font-size:11px;line-height:1.5;font-family:var(--mono,monospace);"></div>
      </div>
    `;
  }

  private renderAgent(cfg: typeof AGENT_CONFIG[0]): string {
    const cls = cfg.key.replace(/\s+/g, '').toLowerCase();
    return `
      <div class="ap-${cls}" style="background:rgba(255,255,255,0.04);border-radius:8px;padding:8px 10px;border:1px solid rgba(255,255,255,0.05);">
        <div style="display:flex;align-items:center;gap:6px;">
          <span style="font-size:16px;">${cfg.icon}</span>
          <span style="font-weight:600;color:${cfg.color};font-size:12px;">${cfg.key}</span>
          <span class="ap-status" style="margin-left:auto;font-size:10px;opacity:0.5;font-family:var(--mono,monospace);">idle</span>
        </div>
        <div class="ap-action" style="font-size:10px;color:${cfg.color};opacity:0.6;margin-top:3px;">—</div>
        <div class="ap-log" style="font-size:10px;opacity:0.4;max-height:24px;overflow:hidden;margin-top:2px;"></div>
        <div style="display:flex;gap:4px;margin-top:6px;flex-wrap:wrap;">
          ${cfg.commands.map((c) => `<button class="ap-cmd-btn" data-cmd="${c.cmd}" data-arg="${c.arg}" style="padding:3px 8px;border-radius:4px;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.08);color:#ccc;font-size:10px;cursor:pointer;">${c.label}</button>`).join('')}
        </div>
      </div>
    `;
  }

  updateAgent(name: string, state: AgentState): void {
    const els = this.agentEls[name];
    if (!els) return;

    const statusIcons: Record<string, string> = {
      idle: '⏸', thinking: '🧠', working: '⚡', done: '✅', error: '❌',
    };
    els.status.textContent = `${statusIcons[state.status] ?? '?'} ${state.status}`;
    els.status.style.opacity = state.status === 'working' ? '1' : '0.6';

    if (state.lastAction) {
      els.action.textContent = `▸ ${state.lastAction.slice(0, 60)}`;
    }
    if (state.lastResult) {
      els.action.textContent = `✓ ${state.lastResult.slice(0, 60)}`;
    }
  }

  addThought(agent: string, thought: string): void {
    const line = document.createElement('div');
    line.textContent = `[${agent}] ${thought.slice(0, 120)}`;
    line.style.cssText = 'opacity:0.7;padding:2px 0;border-bottom:1px solid rgba(255,255,255,0.03);';
    this.thoughtLog.appendChild(line);
    this.thoughtLog.scrollTop = this.thoughtLog.scrollHeight;

    // Keep log bounded
    while (this.thoughtLog.children.length > 50) {
      this.thoughtLog.removeChild(this.thoughtLog.firstChild!);
    }
  }

  addLog(agent: string, msg: string): void {
    const els = this.agentEls[agent];
    if (!els) return;
    els.log.textContent = msg.slice(0, 80);
  }
}
